import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { COLORS } from '../../../utils/constants';
import { 
  BarChart3,
  MessageSquare,
  Gamepad2,
  FileEdit,
  Rocket,
  ClipboardList,
  Trophy,
  PenTool,
  Sparkles,
  Target,
  Heart,
  TrendingUp,
  Clapperboard,
  GraduationCap,
  Search
} from 'lucide-react';

const UseCase = ({ data, onNext, onBack }) => {
  const [formData, setFormData] = useState({
    useCases: data.useCases || [],
    mainGoal: data.mainGoal || ''
  });

  const [errors, setErrors] = useState({});

  const useCaseOptions = [
    { value: 'lead-capture', label: 'Lead capture form', icon: BarChart3 },
    { value: 'customer-feedback', label: 'Customer feedback form', icon: MessageSquare },
    { value: 'quiz', label: 'Quiz / mini-games', icon: Gamepad2 },
    { value: 'application', label: 'Application form', icon: FileEdit },
    { value: 'onboarding', label: 'Onboarding flow', icon: Rocket },
    { value: 'survey', label: 'Survey / poll', icon: ClipboardList },
    { value: 'contest', label: 'Contest form', icon: Trophy },
    { value: 'registration', label: 'Registration form', icon: PenTool },
    { value: 'other', label: 'Something else', icon: Sparkles }
  ];

  const goalOptions = [
    { value: 'leads', label: 'Generate leads', icon: Target },
    { value: 'engagement', label: 'Increase engagement', icon: Heart },
    { value: 'data', label: 'Collect customer data', icon: TrendingUp },
    { value: 'campaign', label: 'Run a gamified campaign', icon: Clapperboard },
    { value: 'educate', label: 'Educate users', icon: GraduationCap },
    { value: 'research', label: 'Research / insights', icon: Search }
  ];

  const toggleUseCase = (value) => {
    const newUseCases = formData.useCases.includes(value)
      ? formData.useCases.filter((v) => v !== value)
      : [...formData.useCases, value];

    setFormData({ ...formData, useCases: newUseCases });
    if (errors.useCases) {
      setErrors({ ...errors, useCases: '' });
    }
  };

  const handleSubmit = () => {
    const newErrors = {};

    if (formData.useCases.length === 0) {
      newErrors.useCases = 'Please select at least one use case';
    }

    if (!formData.mainGoal) {
      newErrors.mainGoal = 'Please select your main goal';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onNext(formData);
  };

  return (
    <div style={{ maxWidth: '750px', margin: '0 auto', padding: '0 clamp(16px, 3vw, 0px)' }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ marginBottom: 'clamp(24px, 4vw, 32px)', textAlign: 'center' }}
      >
        <h2
          style={{
            fontSize: 'clamp(24px, 5vw, 32px)',
            fontWeight: '700',
            color: COLORS.black,
            marginBottom: '8px'
          }}
        >
          What will you create?
        </h2>
        <p style={{ fontSize: 'clamp(14px, 3vw, 16px)', color: COLORS.gray, lineHeight: '1.5' }}>
          Help us understand your needs so we can suggest the best templates
        </p>
      </motion.div>

      {/* Use Cases */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        style={{ marginBottom: 'clamp(24px, 4vw, 32px)' }}
      >
        <label
          style={{
            display: 'block',
            fontSize: 'clamp(13px, 2.5vw, 14px)',
            fontWeight: '600',
            color: COLORS.darkGray,
            marginBottom: '12px'
          }}
        >
          What do you want to create? *{' '}
          <span style={{ color: COLORS.gray, fontWeight: '400' }}>(Select all that apply)</span>
        </label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 'clamp(10px, 2vw, 12px)'
          }}
        >
          {useCaseOptions.map((option, index) => {
            const isSelected = formData.useCases.includes(option.value);
            const IconComponent = option.icon;
            return (
              <motion.button
                key={option.value}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 + index * 0.04, duration: 0.3 }}
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => toggleUseCase(option.value)}
                style={{
                  padding: 'clamp(12px, 2vw, 14px) clamp(10px, 2vw, 12px)',
                  backgroundColor: isSelected ? `${COLORS.blue}15` : COLORS.white,
                  border: `2px solid ${
                    isSelected ? COLORS.blue : errors.useCases ? '#EF4444' : COLORS.lightGray
                  }`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'clamp(6px, 1.5vw, 8px)',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  fontSize: 'clamp(12px, 2.5vw, 13px)',
                  fontWeight: isSelected ? '600' : '500',
                  color: isSelected ? COLORS.blue : COLORS.darkGray,
                  textAlign: 'center',
                  position: 'relative'
                }}
              >
                <IconComponent
                  size={Math.min(28, window.innerWidth * 0.05)}
                  color={isSelected ? COLORS.blue : COLORS.darkGray}
                />
                <span style={{ lineHeight: '1.3' }}>{option.label}</span>
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: COLORS.blue,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 6L5 9L10 3"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>
        {errors.useCases && (
          <p style={{ color: '#EF4444', fontSize: '13px', marginTop: '8px' }}>
            {errors.useCases}
          </p>
        )}
      </motion.div>

      {/* Main Goal */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        style={{ marginBottom: 'clamp(24px, 4vw, 32px)' }}
      >
        <label
          style={{
            display: 'block',
            fontSize: 'clamp(13px, 2.5vw, 14px)',
            fontWeight: '600',
            color: COLORS.darkGray,
            marginBottom: '12px'
          }}
        >
          What is your main goal? *
        </label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 'clamp(10px, 2vw, 12px)'
          }}
        >
          {goalOptions.map((goal, index) => {
            const IconComponent = goal.icon;
            return (
              <motion.button
                key={goal.value}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.05, duration: 0.3 }}
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  setFormData({ ...formData, mainGoal: goal.value });
                  if (errors.mainGoal) {
                    setErrors({ ...errors, mainGoal: '' });
                  }
                }}
                style={{
                  padding: 'clamp(12px, 2vw, 16px)',
                  backgroundColor:
                    formData.mainGoal === goal.value ? `${COLORS.blue}15` : COLORS.white,
                  border: `2px solid ${
                    formData.mainGoal === goal.value
                      ? COLORS.blue
                      : errors.mainGoal
                      ? '#EF4444'
                      : COLORS.lightGray
                  }`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'clamp(8px, 2vw, 12px)',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  fontSize: 'clamp(13px, 2.5vw, 15px)',
                  fontWeight: formData.mainGoal === goal.value ? '600' : '500',
                  color: formData.mainGoal === goal.value ? COLORS.blue : COLORS.darkGray,
                  textAlign: 'left'
                }}
              >
                <IconComponent
                  size={Math.min(24, window.innerWidth * 0.045)}
                  color={formData.mainGoal === goal.value ? COLORS.blue : COLORS.darkGray}
                />
                {goal.label}
              </motion.button>
            );
          })}
        </div>
        {errors.mainGoal && (
          <p style={{ color: '#EF4444', fontSize: '13px', marginTop: '8px' }}>
            {errors.mainGoal}
          </p>
        )}
      </motion.div>

      {/* Navigation Buttons */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.4 }}
        style={{
          display: 'flex',
          gap: 'clamp(10px, 2vw, 12px)',
          marginTop: 'clamp(32px, 5vw, 40px)',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <button
          onClick={onBack}
          style={{
            padding: 'clamp(12px, 2vw, 14px) clamp(20px, 3vw, 28px)',
            backgroundColor: COLORS.white,
            color: COLORS.darkGray,
            border: `2px solid ${COLORS.lightGray}`,
            borderRadius: 'clamp(10px, 2vw, 12px)',
            fontSize: 'clamp(14px, 2.5vw, 15px)',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = COLORS.darkGray;
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = COLORS.lightGray;
            e.target.style.transform = 'translateY(0)';
          }}
        >
          ← Back
        </button>

        <button
          onClick={handleSubmit}
          style={{
            padding: 'clamp(12px, 2vw, 14px) clamp(24px, 4vw, 32px)',
            backgroundColor: COLORS.blue,
            color: COLORS.white,
            border: 'none',
            borderRadius: 'clamp(10px, 2vw, 12px)',
            fontSize: 'clamp(14px, 2.5vw, 15px)',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          }}
        >
          Continue →
        </button>
      </motion.div>
    </div>
  );
};

export default UseCase;